// @flow
// @flow-runtime warn
import React, { type Node, useMemo, useState, useCallback } from 'react';
import clone from 'ramda/es/clone';
import path from 'ramda/es/path';
import prop from 'ramda/es/prop';
import isEmpty from 'ramda/es/isEmpty';
import filter from 'ramda/es/filter';
import reduce from 'ramda/es/reduce';
import pipe from 'ramda/es/pipe';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import action from 'app/common/lib/action';
// $FlowFixMe
import shopApi from 'app/actions/shopApi';
import api from 'app/actions/api';

import OrderContext from './OrderContext';
import { type OrderType, type ReceiverItem } from './OrderTypes';


export type OrderDataProps = {
  children?: Node,
  id: string,
}

const OrderData = ({ children, id }: OrderDataProps) => {
  const { t } = useTranslation('shop');
  const dispatch = useDispatch();

  const [order, setOrder] = useState<OrderType>({});
  const [receivers, setReceivers] = useState<Array<ReceiverItem>>([]);

  const [loadingOrder, setLoadingOrder] = useState(true);
  const [loadingIssue, setLoadingIssue] = useState(false);
  const [loadingReceivers, setLoadingReceivers] = useState(false);

  /**
   * Запрос данных
   */
  const requestOrder = useCallback(async () => {
    const requestPath = `orders/${id}`;
    try {
      setLoadingOrder(true);
      const res = await shopApi(requestPath);
      const orderRes = path(['data', 'order'], res);
      setOrder(orderRes);
      setLoadingOrder(false);
      return true;
    } catch (error) {
      const errorSave = {
        err: t('error_api_order_get'),
        path: requestPath,
        error: clone(error),
      };
      dispatch(action('REQUEST_FAILURE', errorSave));
      return false;
    }
  }, [dispatch, t, id]);

  /**
   * Запрос выдачи товара
   */
  const requestIssue = useCallback(async (items) => {
    const requestPath = `orders/${id}/issue`;
    const rest = {
      data: {
        issue_transactions: items,
      },
    };
    try {
      setLoadingIssue(true);
      const res = await shopApi(requestPath, 'post', rest);
      const orderRes = path(['data', 'order'], res);
      setOrder(orderRes);
      setLoadingIssue(false);
      return true;
    } catch (error) {
      const errorSave = {
        err: t('error_api_order_issue'),
        path: requestPath,
        rest,
        error: clone(error),
      };
      dispatch(action('REQUEST_FAILURE', errorSave));
      return false;
    }
  }, [dispatch, t, id]);

  /**
   * Запрос пользователей, получателей товара
   */

  const requestReceivers = useCallback(async () => {
    if (isEmpty(order)) return false;
    const { receivers: orderReceivers } = order;
    if (!orderReceivers) return false;
    const rest = orderReceivers.map(({ user_uuid: uuid }) => ({ params: { uuid } }));
    const requestPath = 'journal/settings/user_by_uuid';
    try {
      setLoadingReceivers(true);
      const res = await Promise.all(rest.map(_rest => api(requestPath, 'get', _rest)));
      const usersRequested: Array<ReceiverItem> = res.map((_res, i) => ({
        ...path(['data', 'user'], _res),
        name: prop('name', orderReceivers[i]),
      }));
      setReceivers(usersRequested);
      setLoadingReceivers(false);
      return true;
    } catch (error) {
      const errorSave = {
        err: t('error_api_user_by_uuid'),
        path: requestPath,
        rest,
        error: clone(error),
      };
      dispatch(action('REQUEST_FAILURE', errorSave));
      return false;
    }
  }, [order, dispatch, t]);

  /**
   * Товары в заказе, только невыданные
   */

  const orderItemsNotIssued = useMemo(() => {
    const currentStatuses: {
      [key: string]: {
        status: 'issued' | 'not_issued',
        datetime: ?string,
      },
    } = pipe(
      obj => prop('current_issue_statuses', obj) || [],
      reduce((acc, v) => ({ ...acc, ...v }), {}),
    )(order);

    const items = prop('order_items', order) || [];

    return filter(({ id: itemId }) => {
      const currentStatus = path([itemId, 'status'], currentStatuses);
      return (
        currentStatus === 'not_issued'
      );
    }, items);
  }, [order]);

  const contextValue = useMemo(() => ({
    content: {
      order,
      receivers,
      orderItemsNotIssued,
    },
    loading: {
      order: loadingOrder,
      issue: loadingIssue,
      receivers: loadingReceivers,
    },
    request: {
      order: requestOrder,
      issue: requestIssue,
      receivers: requestReceivers,
    },
    update: {
      order: setOrder,
    },
  }), [
    order,
    receivers,
    orderItemsNotIssued,
    loadingOrder,
    loadingIssue,
    loadingReceivers,
    requestOrder,
    requestIssue,
    requestReceivers,
  ]);

  return (
    <OrderContext.Provider value={contextValue}>
      {children}
    </OrderContext.Provider>
  );
};

export default OrderData;
